import type { ExtraFieldTypeModel } from '@/typings/models/ExtraFields';

/**
 * Semantic groups for the visibility settings. Keys align with the
 * extra-field `related_to` vocabulary so standard (string) fields and
 * custom (extra) fields share one grouping code path.
 *
 * This module must stay dependency-free (no import of the sibling
 * `constants` module) — that module participates in an import cycle via
 * CustomerDrawer/LeadFilters. Field keys below are the literal values of
 * LEAD_PROPERTIES; they are frozen because they are persisted in user
 * preferences.
 */
export const FIELD_GROUPS = {
  LEAD: 'lead',
  CUSTOMER: 'customer',
  APPOINTMENT: 'serviceRequest',
  OTHER: 'other',
} as const;

const EXTRA_FIELD_RELATED_TO_GROUP: Record<string, string> = {
  lead: FIELD_GROUPS.LEAD,
  result: FIELD_GROUPS.LEAD,
  customer: FIELD_GROUPS.CUSTOMER,
  serviceRequest: FIELD_GROUPS.APPOINTMENT,
};

export const STANDARD_FIELD_GROUPS: Record<string, string> = {
  uuid: FIELD_GROUPS.LEAD,
  status: FIELD_GROUPS.LEAD,
  priority: FIELD_GROUPS.LEAD,
  dealSize: FIELD_GROUPS.LEAD,
  note: FIELD_GROUPS.LEAD,
  title: FIELD_GROUPS.LEAD,
  createdAt: FIELD_GROUPS.LEAD,
  updatedAt: FIELD_GROUPS.LEAD,
  assignee: FIELD_GROUPS.LEAD,
  comments: FIELD_GROUPS.LEAD,
  leadSource: FIELD_GROUPS.LEAD,
  result: FIELD_GROUPS.LEAD,
  call_tracking: FIELD_GROUPS.LEAD,

  name: FIELD_GROUPS.CUSTOMER,
  email: FIELD_GROUPS.CUSTOMER,
  phone: FIELD_GROUPS.CUSTOMER,
  locale: FIELD_GROUPS.CUSTOMER,
  marketingChannel: FIELD_GROUPS.CUSTOMER,
  marketingSource: FIELD_GROUPS.CUSTOMER,
  marketingCampaign: FIELD_GROUPS.CUSTOMER,
  utm_medium: FIELD_GROUPS.CUSTOMER,
  utm_campaign: FIELD_GROUPS.CUSTOMER,
  customerIntegrations: FIELD_GROUPS.CUSTOMER,
  customerCreatedAt: FIELD_GROUPS.CUSTOMER,
  customerModifiedAt: FIELD_GROUPS.CUSTOMER,

  appointmentAt: FIELD_GROUPS.APPOINTMENT,
  appointmentBranch: FIELD_GROUPS.APPOINTMENT,
  appointmentBranchService: FIELD_GROUPS.APPOINTMENT,
  appointmentServiceType: FIELD_GROUPS.APPOINTMENT,
  product_group: FIELD_GROUPS.APPOINTMENT,
  branch: FIELD_GROUPS.APPOINTMENT,

  // Appointment-table fields (APPOINTMENT_PROPERTIES) that belong to a
  // related entity rather than the appointment itself.
  'appointment:projectCreatedAt': FIELD_GROUPS.LEAD,
  'appointment:projectUpdatedAt': FIELD_GROUPS.LEAD,
  'appointment:lead_source': FIELD_GROUPS.LEAD,
  'appointment:customerCreatedAt': FIELD_GROUPS.CUSTOMER,
  'appointment:customerModifiedAt': FIELD_GROUPS.CUSTOMER,
};

/**
 * Resolves the visibility-settings group of a field. Standard fields are
 * looked up in STANDARD_FIELD_GROUPS (any `appointment:*` key falls back to
 * the Appointment group); extra fields are grouped by their `related_to`.
 */
export const getFieldGroupKey = (field: string | ExtraFieldTypeModel): string => {
  if (typeof field === 'string') {
    if (STANDARD_FIELD_GROUPS[field]) return STANDARD_FIELD_GROUPS[field];
    return field.startsWith('appointment:') ? FIELD_GROUPS.APPOINTMENT : FIELD_GROUPS.OTHER;
  }
  return EXTRA_FIELD_RELATED_TO_GROUP[field.related_to] || FIELD_GROUPS.OTHER;
};
